import React from 'react';
import { Checkbox, Input } from 'antd';
import styled from 'styled-components';

import { MessageMask } from 'types';
import { useIntl } from 'common/config/i18n';
import ListModifier from 'components/organisms/list-modifier';

export type Props = {
  onClickEdit?: (situation: MessageMask) => void;
  isAdmin: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Column<Child extends Column<any> | undefined = undefined> = {
  title: string;
  key?: string;
  render?: (record: MessageMask) => React.ReactNode;
  shouldBeAdmin?: boolean;
  children?: Child[];
};

const CustomCheckbox = styled(Checkbox)<{ cursor?: string }>`
  ${(props) =>
    props.cursor &&
    `
      & {
        .ant-checkbox-input {
          cursor: ${props.cursor};
        }
      }
    `}
`;

export function useColumns({ isAdmin, onClickEdit }: Props): Column<Column>[] {
  const { t } = useIntl();

  const situationCol: Column<Column> = {
    title: t('filtre', 'SITUATION'),
    key: 'situation',
    render: (record: MessageMask) => <div>{record.situationName}</div>,
  };

  const chartReasonCol: Column<Column> = {
    title: t('table', 'CHART_REASON'),
    key: 'chartReason',
    render: (record: MessageMask) => (
      <CustomCheckbox checked={record?.hasReason} cursor="default" style={{ cursor: 'default' }} />
    ),
  };
  const delayCol: Column<Column> = {
    title: t('table', 'COLUMN_DELAY'),
    key: 'delay',
    render: (record: MessageMask) => (
      <CustomCheckbox checked={record?.hasVolumeDelay} cursor="default" style={{ cursor: 'default' }} />
    ),
  };

  const substitutionCol: Column<Column> = {
    title: t('table', 'COLUMN_SUBSTITUTION'),
    key: 'substitution',
    render: (record: MessageMask) => (
      <CustomCheckbox checked={record?.hasSubstituteStation} cursor="default" style={{ cursor: 'default' }} />
    ),
  };
  const quaiCol: Column<Column> = {
    title: t('table', 'COLUMN_QUAI'),
    key: 'quai',
    render: (record: MessageMask) => (
      <CustomCheckbox checked={record?.hasDock} cursor="default" style={{ cursor: 'default' }} />
    ),
  };
  const infoCols: Column<Column> = {
    title: t('table', 'COLUMN_INFO'),
    children: [
      {
        title: t('table', 'COLUMN_LONG'),
        key: 'infoHasPrincipalZoneLongMessage',
        render: (record: MessageMask) => (
          <CustomCheckbox
            checked={record?.hasPrincipalZoneLongMessage}
            cursor="default"
            style={{ cursor: 'default' }}
          />
        ),
      },
      {
        title: t('table', 'COLUMN_COURT'),
        key: 'infoHasPrincipalZoneShortMessage',
        render: (record: MessageMask) => (
          <CustomCheckbox
            checked={record?.hasPrincipalZoneShortMessage}
            cursor="default"
            style={{ cursor: 'default' }}
          />
        ),
      },
    ],
  };
  const infoCompCols: Column<Column> = {
    title: t('table', 'COLUMN_INFO_COMP'),
    children: [
      {
        title: t('table', 'COLUMN_LONG'),
        key: 'infoCompHasSecondaryZoneLongMessage',
        render: (record: MessageMask) => (
          <CustomCheckbox
            checked={record?.hasSecondaryZoneLongMessage}
            cursor="default"
            style={{ cursor: 'default' }}
          />
        ),
      },
      {
        title: t('table', 'COLUMN_COURT'),
        key: 'infoCompHasSecondaryZoneShortMessage',
        render: (record: MessageMask) => (
          <CustomCheckbox
            checked={record?.hasSecondaryZoneShortMessage}
            cursor="default"
            style={{ cursor: 'default' }}
          />
        ),
      },
    ],
  };

  const compCols: Column<Column> = {
    title: t('table', 'COLUMN_COMP'),
    children: [
      {
        title: t('table', 'COLUMN_ALL'),
        key: 'compHasCompensationZoneForAll',
        render: (record: MessageMask) => (
          <CustomCheckbox checked={record?.hasCompensationZoneForAll} cursor="default" style={{ cursor: 'default' }} />
        ),
      },
      {
        title: t('table', 'COLUMN_UP'),
        key: 'compHasCompensationZoneForUp',
        render: (record: MessageMask) => (
          <CustomCheckbox checked={record?.hasCompensationZoneForUp} cursor="default" style={{ cursor: 'default' }} />
        ),
      },
      {
        title: t('table', 'COLUMN_DOWN'),
        key: 'compHasCompensationZoneForDown',
        render: (record: MessageMask) => (
          <CustomCheckbox checked={record?.hasCompensationZoneForDown} cursor="default" style={{ cursor: 'default' }} />
        ),
      },
      {
        title: t('table', 'COLUMN_UP_DOWN'),
        key: 'compHasCompensationZoneForUpOrDown',
        render: (record: MessageMask) => (
          <CustomCheckbox
            checked={record?.hasCompensationZoneForUpOrDown}
            cursor="default"
            style={{ cursor: 'default' }}
          />
        ),
      },
      {
        title: t('table', 'COLUMN_DV_NUMBERS'),
        key: 'compHasCompensationZoneForSpecificBookings',
        render: (record: MessageMask) => (
          <CustomCheckbox
            checked={record?.hasCompensationZoneForSpecificBookings}
            cursor="default"
            style={{ cursor: 'default' }}
          />
        ),
      },
    ],
  };

  const signatureCol: Column<Column> = {
    title: t('form', 'SIGNATURE_H4'),
    key: 'signature',
    render: (record: MessageMask) => (
      <div style={{ display: 'flex' }}>
        <CustomCheckbox
          style={{ marginRight: 10, cursor: 'default' }}
          checked={record?.hasSigningZone}
          cursor="default"
        />
        <Input value={record?.signingContent} />
      </div>
    ),
  };

  const editCol: Column<Column> = {
    title: t('table', 'COLUMN_EDIT'),
    key: 'signature',
    shouldBeAdmin: true,
    render: (record: MessageMask) => {
      return <ListModifier hideDeleteButton hideEditButton={!isAdmin} onEdit={() => onClickEdit?.(record)} />;
    },
  };

  const Columns = [
    { ...situationCol },
    { ...chartReasonCol },
    { ...delayCol },
    { ...substitutionCol },
    { ...quaiCol },
    { ...infoCols },
    { ...infoCompCols },
    { ...compCols },
    { ...signatureCol },
    { ...editCol },
  ];

  return Columns;
}
